import { observable, set, action, computed } from 'mobx';
import { isEqual } from 'lodash';
import type { RootStore } from '~/stores/root.store';
import {
    EMPTY_AUTOMATION_INSTANCE,
    IAutomationInstance
} from '../types/automation.types';
import { AutomationStep } from './automations-steps/automation-step.model';
import { prepClassForServer } from '../utils/data.utils';
import {
    AutomationActionFileType,
    AutomationActionType
} from '../components/Automations/AutomationsEnums';
import { TShortTimezone } from '../utils/timezone.utils';

export class Automation implements IAutomationInstance {
    @observable _id: string;
    @observable title: string;
    @observable applied_date: string;
    @observable clone_from_template: string;
    @observable is_template: boolean;
    @observable project_date: string;
    @observable project_end_date: string;
    @observable project_id: string;
    @observable project_timezone: TShortTimezone;
    @observable workspace_id: string;
    @observable relevant_project_types: string[];
    @observable workflow_steps: AutomationStep[];

    initValues: IAutomationInstance;
    rootStore: RootStore;

    constructor(automation: IAutomationInstance, rootStore: RootStore) {
        this.rootStore = rootStore;
        set(this, automation);
        this.workflow_steps = (automation.workflow_steps || []).map(
            (step, index) =>
                new AutomationStep(
                    { ...step, order: index },
                    automation._id,
                    this.rootStore
                )
        );
        this.initValues = this.getDataForServer();
    }

    @computed
    get hasSteps(): boolean {
        return !!this.workflow_steps?.length;
    }

    @computed
    get isValid() {
        return (
            this.title &&
            this.workflow_steps.length &&
            this.workflow_steps.every(step => step.isValid)
        );
    }

    @computed
    get isDirty() {
        return !isEqual(this.initValues, this.getDataForServer());
    }

    @computed
    get stepsWithScheduleTriggers() {
        return this.workflow_steps.filter(step => step.isScheduleTrigger);
    }

    @action.bound
    setTitle(title: string) {
        this.title = title;
    }

    @action.bound
    addAutomationStep(
        type: AutomationActionType,
        file_type: AutomationActionFileType,
        placement?: number
    ) {
        const order = placement || this.workflow_steps.length;
        const step = new AutomationStep(
            {
                order,
                approval_status: type === 'task' ? 'required' : 'not_required',
                workflow_step_action: file_type
                    ? { type, file_type }
                    : { type },
                workflow_step_trigger: {
                    delay_temporal_amount: 0,
                    delay_temporal_unit: 'days',
                    trigger_after_step: null,
                    trigger_type:
                        order > 0
                            ? 'after_step_execution'
                            : 'after_workflow_execution'
                }
            },
            this._id,
            this.rootStore
        );
        if (order === this.workflow_steps.length) {
            this.workflow_steps.push(step);
        } else {
            this.workflow_steps.splice(order, 0, step);
        }
        this.updateStepsOrder();
        return step;
    }

    @action.bound
    deleteAutomationStep(automationStep: AutomationStep) {
        const stepIndex = this.workflow_steps.indexOf(automationStep);
        this.workflow_steps.splice(stepIndex, 1);
        this.updateStepsOrder();
    }

    @action.bound
    duplicateAutomationStep(automationStep: AutomationStep) {
        const { _id: omit, ...step } = automationStep.getDataForServer();
        const duplicatedStep = new AutomationStep(
            { ...step, order: step.order + 1 },
            this._id,
            this.rootStore
        );
        this.workflow_steps.splice(step.order + 1, 0, duplicatedStep);
        this.updateStepsOrder();
        return duplicatedStep;
    }

    @action.bound
    changePositionAutomationBlock(
        automationStep: AutomationStep,
        newIndex: number
    ) {
        const stepIndex = this.workflow_steps.indexOf(automationStep);
        this.workflow_steps.splice(
            newIndex,
            0,
            this.workflow_steps.splice(stepIndex, 1)[0]
        );
    }

    @action.bound
    updateStepsOrder() {
        this.workflow_steps.forEach((step, index) => step.updateOrder(index));
    }

    getDataForServer(): IAutomationInstance {
        const dataForServer = prepClassForServer(this, ['initValues']);
        if (dataForServer._id === EMPTY_AUTOMATION_INSTANCE) {
            delete dataForServer._id;
        }
        dataForServer.workflow_steps = this.workflow_steps.map(step =>
            step.getDataForServer()
        );
        return dataForServer;
    }
}
